/**
 * mutation-fx.ts — Phaser-free pure helpers for the Phase-1 質變 (mutation)
 * upgrades (彈跳 bounce + 爆炸 explode).
 *
 * 鐵則(學習與遊戲分離):pure math only. No Phaser, no scene, no progress /
 * 修煉值. The scene reads RunModifiers flags and calls these to compute WHERE a
 * bounced bullet should go and WHICH enemies an explosion catches; the scene
 * itself does the rendering + damage application.
 */

/** Minimal positional target (an enemy / point) for pure geometry. */
export interface Point {
  x: number;
  y: number;
}

/** A live enemy candidate for bounce targeting / AoE. */
export interface BounceTarget extends Point {
  /** Pass only alive enemies; callers filter, but we double-guard. */
  alive?: boolean;
}

/**
 * Pick the nearest still-alive enemy to `from`, EXCLUDING the one just hit
 * (`excludeIndex`). Returns the index into `enemies`, or -1 if none qualifies.
 *
 * Pure: takes plain {x,y} points so it unit-tests without a scene.
 */
export function nearestOtherEnemyIndex(
  from: Point,
  enemies: BounceTarget[],
  excludeIndex: number,
): number {
  let best = -1;
  let bestD2 = Infinity;
  for (let i = 0; i < enemies.length; i++) {
    if (i === excludeIndex) continue;
    const e = enemies[i];
    if (e.alive === false) continue;
    const dx = e.x - from.x;
    const dy = e.y - from.y;
    const d2 = dx * dx + dy * dy;
    if (d2 < bestD2) {
      bestD2 = d2;
      best = i;
    }
  }
  return best;
}

/**
 * Compute the new (vx, vy) for a bullet bouncing FROM `from` toward `target`,
 * preserving its current speed (|v|). If the bullet has ~zero speed, falls back
 * to `fallbackSpeed`. Returns a velocity pointing at the target.
 *
 * Pure: no mutation of inputs.
 */
export function redirectVelocityToward(
  from: Point,
  target: Point,
  curVx: number,
  curVy: number,
  fallbackSpeed = 600,
): { vx: number; vy: number } {
  let speed = Math.hypot(curVx, curVy);
  if (!(speed > 0)) speed = fallbackSpeed;
  let dx = target.x - from.x;
  let dy = target.y - from.y;
  const dist = Math.hypot(dx, dy);
  if (!(dist > 0)) {
    // Degenerate: target is exactly on the bullet — keep horizontal travel.
    return { vx: speed, vy: 0 };
  }
  dx /= dist;
  dy /= dist;
  return { vx: dx * speed, vy: dy * speed };
}

/**
 * Reflect a velocity off a horizontal wall (top/bottom): flips vy. Used when
 * the bullet bounces off the arena's top/bottom edge with no other enemy to
 * chase. (Left/right walls aren't reflectors for a right-travelling bullet.)
 */
export function reflectVerticalWall(vx: number, vy: number): { vx: number; vy: number } {
  return { vx, vy: -vy };
}

/**
 * 爆炸彈 AoE damage scaling. The explosion radius grows +40px per stacked
 * 爆炸彈 pick (see RunModifiers.explodeOnHitRadius += 40), so we can infer the
 * stack count from the radius and make each extra pick hit HARDER, not just
 * wider. This is what makes picking 爆炸彈 a SECOND time feel stronger.
 *
 * stacks = round(radius / 40), clamped to ≥1.
 * multiplier = 0.6 + 0.4 * stacks  →  1 stack ×1.0, 2 ×1.4, 3 ×1.8.
 *
 * Pure: integer-rounded damage, no Phaser.
 */
export function explodeDamageForRadius(baseDmg: number, radius: number): number {
  const stacks = Math.max(1, Math.round(radius / 40));
  return Math.round(baseDmg * (0.6 + 0.4 * stacks));
}

/**
 * 彈跳彈 ricochet off the boss. A bullet that hits the boss with bounces left
 * reverses its horizontal direction and gains a small vertical kick so the
 * bounce is visibly a bounce (not just a straight reversal). The kid sees the
 * bullet ping off and come back for another hit. Each extra 彈跳彈 pick = more
 * bounces = more boss hits, so picking it twice feels stronger.
 *
 * - vx flips sign (bounce back the way it came).
 * - vy gets a small vertical component: keep its existing sign if it already
 *   had one, otherwise nudge it down (+) by a fraction of the bullet's speed.
 *
 * Pure: no mutation of inputs.
 */
export function ricochetOffBoss(vx: number, vy: number): { vx: number; vy: number } {
  const speed = Math.hypot(vx, vy) || Math.abs(vx) || 600;
  const kick = speed * 0.35;
  const newVy = vy !== 0 ? (vy > 0 ? kick : -kick) : kick;
  return { vx: -vx, vy: newVy };
}

/**
 * Which enemy indices fall within `radius` of the explosion `center`. Excludes
 * the directly-hit enemy (`excludeIndex`) since the scene already damaged it.
 * Returns indices so the caller can apply AoE damage to each.
 *
 * Pure: distance-squared compare, no allocation beyond the result array.
 */
export function enemiesInBlastRadius(
  center: Point,
  radius: number,
  enemies: BounceTarget[],
  excludeIndex: number,
): number[] {
  const hit: number[] = [];
  if (!(radius > 0)) return hit;
  const r2 = radius * radius;
  for (let i = 0; i < enemies.length; i++) {
    if (i === excludeIndex) continue;
    const e = enemies[i];
    if (e.alive === false) continue;
    const dx = e.x - center.x;
    const dy = e.y - center.y;
    if (dx * dx + dy * dy <= r2) hit.push(i);
  }
  return hit;
}
